import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, useFetch } from "../lib/api";
import { useApp } from "../context/AppContext";
import { AuthLayout } from "./Login";

const STATUS_MSG = {
  accepted: "Cette invitation a déjà été utilisée.",
  expired: "Cette invitation a expiré. Demandez un nouveau lien à votre responsable.",
  revoked: "Cette invitation a été révoquée.",
};

function RegisterForm({ token, onDone }) {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "", accept_terms: false });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.accept_terms) return toast.error("Veuillez accepter les conditions d'utilisation");
    setBusy(true);
    try {
      const { data } = await api.post(`/invite/${token}/register`, f);
      onDone(data);
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="invite-register-form">
      <div className="grid grid-cols-2 gap-3">
        <label><span className="label">Prénom</span><input className="field" required value={f.first_name} onChange={set("first_name")} data-testid="register-first-name-input" /></label>
        <label><span className="label">Nom</span><input className="field" required value={f.last_name} onChange={set("last_name")} data-testid="register-last-name-input" /></label>
      </div>
      <label className="block"><span className="label">Adresse e-mail</span><input className="field" type="email" required value={f.email} onChange={set("email")} data-testid="register-email-input" /></label>
      <label className="block"><span className="label">Téléphone (facultatif)</span><input className="field" type="tel" value={f.phone} onChange={set("phone")} data-testid="register-phone-input" /></label>
      <label className="block"><span className="label">Mot de passe (8 caractères min.)</span><input className="field" type="password" minLength={8} required value={f.password} onChange={set("password")} data-testid="register-password-input" /></label>
      <label className="flex min-h-[48px] items-start gap-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
        <input type="checkbox" className="mt-0.5 h-5 w-5 accent-teal-600" checked={f.accept_terms} onChange={set("accept_terms")} data-testid="register-terms-checkbox" />
        <span>J'accepte les <a href="/legal" target="_blank" className="font-semibold text-teal-700 underline">conditions d'utilisation et la politique de confidentialité</a>. Aucune donnée médicale ni information patient n'est enregistrée.</span>
      </label>
      <button className="btn-primary w-full" disabled={busy} data-testid="register-submit-btn">{busy ? "Création…" : "Créer mon compte"}</button>
    </form>
  );
}

export default function InvitePage() {
  const { token } = useParams();
  const { me, setMe, setSid } = useApp();
  const nav = useNavigate();
  const { data, error } = useFetch(`/invite/${token}`);
  const [step, setStep] = useState("intro");

  const finish = (payload) => {
    setMe({ user: payload.user, structures: payload.structures });
    const joined = payload.structure_id || payload.structures?.[payload.structures.length - 1]?.id;
    if (joined) setSid(joined);
    toast.success("Bienvenue dans l'équipe !");
    nav("/disponibilites", { replace: true });
  };

  const accept = async () => {
    try {
      const { data: d } = await api.post(`/invite/${token}/accept`);
      finish(d);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (error) return <AuthLayout title="Invitation introuvable"><p className="text-slate-600">{error}</p></AuthLayout>;
  if (!data) return <AuthLayout title="Chargement…"><div className="h-20 animate-pulse rounded-xl bg-slate-100" /></AuthLayout>;

  return (
    <AuthLayout title="Vous êtes invitée à rejoindre" subtitle={null}>
      <div className="mb-5 rounded-2xl bg-teal-50 p-4" data-testid="invite-structure-info">
        <div className="font-display text-2xl font-extrabold text-teal-900" data-testid="invite-structure-name">{data.structure_name}</div>
        <div className="mt-1 text-sm text-teal-800">Par <b>{data.inviter_name}</b></div>
      </div>
      {data.status !== "pending" ? (
        <p className="text-slate-700" data-testid="invite-invalid-msg">{STATUS_MSG[data.status]}</p>
      ) : me ? (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">Connectée en tant que <b>{me.user.email}</b>.</p>
          <button className="btn-primary w-full" onClick={accept} data-testid="invite-accept-btn">Accepter l'invitation</button>
        </div>
      ) : step === "intro" ? (
        <div className="space-y-3">
          <button className="btn-primary w-full" onClick={() => setStep("form")} data-testid="invite-join-btn">Rejoindre l'équipe</button>
          <Link to={`/login?next=/invite/${token}`} className="btn-ghost w-full" data-testid="invite-login-link">J'ai déjà un compte</Link>
        </div>
      ) : (
        <RegisterForm token={token} onDone={finish} />
      )}
    </AuthLayout>
  );
}
